/** @type {import("./types.js").InfoPagesCatalog} */
export const infoPagesNl = {
  about: {
    id: "about",
    kicker: "Over Plaatplan",
    title: "Waarom Plaatplan?",
    intro: "Plaatplan is gemaakt voor doe-het-zelvers die in één sessie meerdere projecten uit hetzelfde plaatmateriaal plannen, met een helder werkplan voor de zaag.",
    sections: [
      {
        heading: "Het probleem",
        paragraphs: [
          "Veel tools gaan uit van één project of één zaaglijst. In de praktijk bouw je vaak meerdere dingen tegelijk: een kast, een bureau en nog iets uit dezelfde MDF-plaat.",
          "Dan wil je snel weten hoeveel je moet bestellen, hoe onderdelen op de platen liggen, en wat je aan de bouwmarkt of zaagservice kunt meegeven.",
        ],
      },
      {
        heading: "Wat Plaatplan wél is",
        paragraphs: [
          "Een planner voor meerdere deelprojecten op één standaardplaat, met optioneel restmateriaal dat je al hebt liggen.",
          "Een vendor-neutraal werkplan met zaaglijst, maten en kantafwerking, bruikbaar bij elke zaagservice of bouwmarkt.",
          "Een tool die lokaal in je browser werkt. Geen account, geen cloud.",
        ],
      },
      {
        heading: "Wat Plaatplan niet is",
        paragraphs: [
          "Geen live zaagcoach aan de machine. Plaatplan helpt je plannen vóór het inkopen en in het werkplan kun je op desktop de zaagvolgorde controleren, niet snede voor snede terwijl je zaagt.",
          "Geen bouwmarkt- of merk-koppeling. Je exporteert een generiek werkplan.",
          "Geen industriële optimizer. De indeling is betrouwbaar en begrijpelijk, niet altijd theoretisch optimaal.",
        ],
      },
      {
        heading: "Voor wie",
        paragraphs: [
          "DHZ'ers die kasten, nissen of interieurdelen maken uit MDF, multiplex of spaanplaat, en het materiaal deels laten zagen of zelf zaagt.",
        ],
      },
    ],
  },
  how: {
    id: "how",
    kicker: "Handleiding",
    title: "Hoe het werkt",
    intro: "Van materiaal tot werkplan in vier stappen.",
    steps: [
      {
        title: "1. Materiaalvoorraad instellen",
        body: "Kies standaardplaat (formaat, materiaal, nerfrichting) en stel de zaagbreedte (kerf) in. Voeg eventueel restmateriaal toe dat je al hebt.",
      },
      {
        title: "2. Deelprojecten en onderdelen invoeren",
        body: "Maak per project een deelproject. Voer panelen in via de tabel of de regel L×B×aantal naam.",
      },
      {
        title: "3. Zaagindeling controleren",
        body: "Bekijk hoeveel standaardplaten je nodig hebt, welk restmateriaal wordt gebruikt, en of alles past.",
      },
      {
        title: "4. Werkplan exporteren",
        body: "Open het werkplan via Exporteren → Werkplan openen. Stel in onder Voorbereiden, controleer de Zaagopdracht en exporteer als PDF.",
      },
    ],
  },
  faq: {
    id: "faq",
    kicker: "Hulp",
    title: "Veelgestelde vragen",
    intro: "Antwoorden op veelvoorkomende vragen over Plaatplan.",
    items: [
      {
        id: "storage",
        question: "Waar wordt mijn plan opgeslagen?",
        answer: "Lokaal in deze browser (localStorage). Er is geen account en geen cloud. Maak regelmatig een backup via Exporteren → Projectbestand (.json). Zie ook Importeren en exporteren in de handleiding.",
      },
      {
        id: "takeToStore",
        question: "Kan ik dit meenemen naar de bouwmarkt of zaagservice?",
        answer: "Ja. Gebruik Exporteren → Werkplan openen voor het werkplan, of Wat heb ik nodig voor een inkoopoverzicht. Zie de handleiding Werkplan printen.",
      },
      {
        id: "kerf",
        question: "Houdt Plaatplan rekening met zaagbreedte (kerf)?",
        answer: "Ja. De indeling is millimeternauwkeurig en houdt rekening met kerf. De standaard is 2,2 mm; pas dit aan bij Zaaginstellingen als je zaagblad of zaagservice afwijkt.",
      },
      {
        id: "restStock",
        question: "Wat is het verschil tussen standaardplaat en restmateriaal?",
        answer: "Standaardplaat is wat je nieuw bestelt (bijv. 2440 × 1220 mm). Restmateriaal zijn stukken die je al hebt. De planner gebruikt restmateriaal vóór nieuwe platen.",
      },
      {
        id: "rotation",
        question: "Mogen panelen draaien? Wat is nerfrichting?",
        answer: "Bij MDF en spaanplaat mag je panelen meestal 90° draaien. Bij zichtbaar multiplex zet je nerfrichting vast, zodat panelen niet ongewenst draaien.",
      },
      {
        id: "accuracy",
        question: "Hoe nauwkeurig is de indeling?",
        answer: "Plaatplan gebruikt een snelle, begrijpelijke indeling, geen industriële optimizer. Controleer altijd of aantallen en maten kloppen vóór je materiaal bestelt.",
      },
      {
        id: "multiProject",
        question: "Kan ik meerdere projecten in één plan zetten?",
        answer: "Ja. Elk project is een deelproject. Onderdelen met dezelfde dikte kunnen op dezelfde plaat terechtkomen, kleurgecodeerd per project.",
      },
      {
        id: "doesNotFit",
        question: "Wat als een onderdeel niet past?",
        answer: "Je ziet een waarschuwing bij Resultaat. Pas afmetingen, standaardplaat of restmateriaal aan tot alles past.",
      },
      {
        id: "exportStrategies",
        question: "Wat zijn de exportstrategieën in het werkplan?",
        answer: "Efficiënt zagen, grote zaagrest bewaren en stroken leveren beïnvloeden de instructies en het werkplan, niet welke panelen waar op de plaat liggen. Zet Zaagsneden tonen aan om genummerde snedes en de zaagvolgorde-preview te gebruiken.",
      },
      {
        id: "cutSequence",
        question: "Hoe controleer ik de zaagvolgorde?",
        answer: "In het werkplan (tab Voorbereiden) kun je op desktop per plaat via Volgorde bekijken de zaagvolgorde stap voor stap controleren. Zet Zaagsneden tonen aan bij zaaginstellingen. Op een smal scherm zie je alleen het statische plaatschema. Zie de handleiding Zaagvolgorde controleren.",
      },
      {
        id: "demo",
        question: "Hoe laad ik het voorbeeld?",
        answer: "Klik op Demo laden in de zaagindeling, of importeer een eerder opgeslagen projectbestand via Importeren in de header. Meer uitleg staat in de handleiding.",
      },
      {
        id: "feedback",
        question: "Hoe kan ik feedback geven?",
        answer: "Open Feedback in de footer. Via het formulier kun je een bug, idee of vraag sturen. Vul je e-mail alleen in als je antwoord wilt ontvangen.",
      },
      {
        id: "support",
        question: "Kan ik Plaatplan steunen?",
        answer: "Ja, vrijwillig. In de footer staat “Steun Plaatplan”. Daar kun je kiezen uit een financiële bijdrage (bijv. via Tikkie), het delen van de site, feedback geven, en, wanneer beschikbaar, de werkplaatsmodus-app proberen. De web-planner blijft gratis; er is geen verplichting.",
      },
    ],
  },
  importExport: {
    id: "importExport",
    kicker: "Handleiding",
    title: "Project importeren en exporteren",
    intro:
      "Je plan wordt automatisch opgeslagen in deze browser. Met een projectbestand (.json) kun je een backup maken, je werk overzetten naar een ander apparaat, of later verder gaan waar je was gebleven.",
    sections: [
      {
        heading: "Automatisch opslaan",
        paragraphs: [
          "Plaatplan bewaart je plan lokaal in de browser (localStorage). Je hoeft niet handmatig op te slaan. Wis je sitegegevens of gebruik je een andere browser, dan is dat plan weg.",
          "Maak daarom regelmatig een projectbestand als backup, vooral vóór je browser opschoont of je op een ander apparaat verder wilt.",
        ],
      },
      {
        heading: "Projectbestand exporteren",
        paragraphs: [
          "Klik op Exporteren in de header en kies Projectbestand. Je krijgt een .json-bestand met je plan, deelprojecten, zaaginstellingen, restmateriaal en een momentopname van de zaagindeling.",
          "Het bestand heet ongeveer jouw-plan-naam-project.json. Bewaar het op je computer, in cloudopslag of stuur het naar jezelf per e-mail.",
        ],
      },
      {
        heading: "Project importeren",
        paragraphs: [
          "Klik op Importeren in de header en kies een eerder gedownload .json-bestand. Je huidige plan in deze browser wordt vervangen door het geïmporteerde plan.",
          "Controleer na importeren of maten, aantallen en zaaginstellingen kloppen. De zaagindeling wordt opnieuw berekend op basis van je invoer.",
        ],
      },
      {
        heading: "Andere exporten (geen backup)",
        paragraphs: [
          "Via Exporteren kun je ook een printbaar werkplan (PDF), een zaaglijst als CSV of een inkoopoverzicht downloaden. Die bestanden zijn bedoeld voor de bouwmarkt, zaagservice of je eigen administratie.",
          "Ze bevatten niet alles wat in een projectbestand zit. Wil je je plan later weer bewerken in Plaatplan, gebruik dan altijd het .json-projectbestand.",
        ],
      },
      {
        heading: "Tips",
        paragraphs: [
          "Maak vóór importeren een backup van je huidige plan als je dat nog nodig hebt.",
          "Een projectbestand werkt op elk apparaat met een moderne browser. Er is geen account nodig.",
          "Deel geen projectbestand publiek als er persoonsgegevens in projectnamen of notities staan.",
        ],
      },
    ],
  },
  feedback: {
    id: "feedback",
    kicker: "Contact",
    title: "Feedback",
    intro: "Mis je iets, werkt iets niet, of heb je een idee? Laat het weten. Dat helpt Plaatplan beter te maken.",
  },
  privacy: {
    id: "privacy",
    kicker: "Vertrouwen",
    title: "Privacy & data",
    intro: "Plaatplan is ontworpen om zo min mogelijk gegevens te verzamelen.",
    sections: [
      {
        heading: "Lokaal opgeslagen",
        paragraphs: [
          "Je plan wordt automatisch opgeslagen in deze browser. Er gaat niets naar een server van Plaatplan. Er is geen server-side opslag en geen account.",
        ],
      },
      {
        heading: "Wat jij exporteert",
        paragraphs: [
          "Alleen wanneer jij zelf exporteert (PDF, CSV, JSON) verlaat data je apparaat. Die bestanden beheer je zelf.",
        ],
      },
      {
        heading: "Bezoektelling",
        paragraphs: [
          "Plaatplan gebruikt geen cookies voor advertenties of profilering. Om te zien of de site nuttig is, tellen we anoniem hoeveel pagina's worden bekeken via GoatCounter. Daarbij wordt geen persoonlijk profiel opgebouwd en is geen cookiebanner nodig.",
          "Meer informatie: https://www.goatcounter.com/help/privacy.",
        ],
      },
      {
        heading: "Vrijwillige steun",
        paragraphs: [
          "Via “Steun Plaatplan” in de footer kun je vrijwillig helpen: financieel (bijv. Tikkie of Mollie), door de gratis web-planner te delen, feedback te geven, of, wanneer beschikbaar, de app voor werkplaatsmodus te proberen. Betalen gebeurt buiten Plaatplan, in een nieuw tabblad.",
        ],
      },
      {
        heading: "Andere browser of apparaat",
        paragraphs: [
          "Je plan is gekoppeld aan deze browser op dit apparaat. Gebruik Projectbestand (.json) om te back-uppen of over te zetten.",
        ],
      },
      {
        heading: "Feedbackformulier",
        paragraphs: [
          "Als je feedback stuurt, gaan alleen je bericht, het type melding en optioneel je e-mailadres mee. Je zaagplan zelf wordt niet verstuurd.",
        ],
      },
    ],
  },
  disclaimer: {
    id: "disclaimer",
    kicker: "Vertrouwen",
    title: "Disclaimer & gebruik",
    intro:
      "Plaatplan is een gratis hulpmiddel in je browser. Lees kort door wat je wel en niet mag verwachten.",
    sections: [
      {
        heading: "Hulpmiddel, geen professioneel advies",
        paragraphs: [
          "Plaatplan helpt je plannen en exporteren. Het is geen bouwkundig, zaag- of veiligheidsadvies. Gebruik je eigen oordeel bij het inkopen en zagen van materiaal.",
        ],
      },
      {
        heading: "Jij controleert maten en aantallen",
        paragraphs: [
          "Controleer altijd of maten, aantallen, kerf, nerfrichting en kantafwerking kloppen vóór je bestelt of zaagt. Fouten in invoer of interpretatie kunnen tot verkeerde platen of verspilling leiden.",
        ],
      },
      {
        heading: "Gebruik op eigen risico",
        paragraphs: [
          "De planner en exporten worden aangeboden zoals ze zijn. Er is geen garantie dat de indeling optimaal, foutloos of geschikt is voor jouw situatie.",
        ],
      },
      {
        heading: "Beperkte aansprakelijkheid",
        paragraphs: [
          "Plaatplan is niet aansprakelijk voor materiaalverlies, schade, kosten of andere gevolgen door gebruik van de tool of door exporten (PDF, CSV, projectbestand).",
        ],
      },
      {
        heading: "Intellectueel eigendom",
        paragraphs: [
          "De software en inhoud van Plaatplan blijven eigendom van de maker. Je mag de tool vrij gebruiken voor je eigen projecten. Hergebruik of doorverkoop van de software zelf is niet toegestaan zonder toestemming.",
        ],
      },
      {
        heading: "Feedback",
        paragraphs: [
          "Wat je via het feedbackformulier stuurt, gebruiken we om Plaatplan te verbeteren. Stuur geen gevoelige gegevens mee die je niet wilt delen.",
        ],
      },
    ],
  },
};
